import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { Badge, Button, Mono, Text, Textarea } from "@athena/ui";
import { AdminVideoAssetDocument, SetCaptionStatusDocument, UpdateCaptionDocument } from "@/graphql/media.generated";

/**
 * Admin caption editor (slice 4.1.5). Lists an asset's caption tracks; each can
 * be edited (the raw WebVTT is fetched from its signed URL, edited, and saved
 * back via `updateCaption`) and published/unpublished (`setCaptionStatus`).
 *
 * Auto-generated tracks arrive PUBLISHED + AUTO; a save flips them to EDITED.
 */

interface Caption {
    id: string;
    language: string;
    source: string;
    status: string;
    vttUrl?: string | null;
}

export function CaptionEditor({ assetId }: { assetId: string }) {
    const { data, loading } = useQuery(AdminVideoAssetDocument, { variables: { id: assetId } });
    const captions = data?.videoAsset?.captions ?? [];

    // Keyed on data, not `loading`: Apollo sets loading on every refetch, and this
    // editor refetches after each caption save — gating on it alone threw away the
    // list (and any edit in progress) between saves.
    if (loading && !data)
        return (
            <Text muted size="sm">
                Loading captions…
            </Text>
        );
    if (captions.length === 0)
        return (
            <Text muted size="sm">
                No captions yet — they appear once transcription completes.
            </Text>
        );

    return (
        <div className="space-y-4">
            {captions.map((c) => (
                <SingleCaptionEditor key={c.id} caption={c} />
            ))}
        </div>
    );
}

function SingleCaptionEditor({ caption }: { caption: Caption }) {
    // null = still loading the VTT text; "" = loaded/empty.
    const [
        text,
        setText,
    ] = useState<string | null>(null);
    const [
        dirty,
        setDirty,
    ] = useState(false);
    const [
        updateCaption,
        { loading: saving },
    ] = useMutation(UpdateCaptionDocument);
    const [
        setStatus,
        { loading: statusSaving },
    ] = useMutation(SetCaptionStatusDocument);

    useEffect(() => {
        let active = true;
        if (!caption.vttUrl) {
            setText("");
            return;
        }
        fetch(caption.vttUrl)
            .then((r) => r.text())
            .then((t) => {
                if (active) setText(t);
            })
            .catch(() => {
                if (active) setText("");
            });
        return () => {
            active = false;
        };
    }, [caption.vttUrl]);

    const published = caption.status === "PUBLISHED";

    return (
        <div className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between">
                <Mono size="xs" className="uppercase">
                    {caption.language} · {caption.source}
                </Mono>
                <Badge variant={published ? "default" : "secondary"}>{caption.status}</Badge>
            </div>
            <Textarea
                aria-label={`Captions (${caption.language})`}
                value={text ?? ""}
                disabled={text === null}
                rows={10}
                onChange={(e) => {
                    setText(e.target.value);
                    setDirty(true);
                }}
            />
            <div className="flex gap-2">
                <Button
                    size="sm"
                    disabled={saving || text === null || !dirty}
                    onClick={async () => {
                        await updateCaption({ variables: { id: caption.id, vtt: text ?? "" } });
                        setDirty(false);
                    }}
                >
                    {saving ? "Saving…" : "Save"}
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    disabled={statusSaving}
                    onClick={() =>
                        setStatus({ variables: { id: caption.id, status: published ? "DRAFT" : "PUBLISHED" } })
                    }
                >
                    {published ? "Unpublish" : "Publish"}
                </Button>
            </div>
        </div>
    );
}
